import React, { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router';
import { routes } from '@/constant/routes.const';
import { IGlobalState, initialGlobalState } from '@/store';
import styled from 'styled-components';
import { debounce } from 'lodash-es';
import { verifyPassport, verifyStorePassport } from '@/api';
import PassportInputForm from '@/components/PassportValidate/PassportInputForm';
import ReceiptLayout from '@/components/common/ReceiptLayout';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import {
  IVerifyPassportPayload,
  IVerifyPassportResponse,
} from '@/types/common.type';
import * as Sentry from '@sentry/react';

export interface IErrorState {
  message: string;
}
function PassportValidate() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { passportNumber: currPassportNumber } = useAppSelector(
    (state) => state,
  ) as IGlobalState;

  const formMethod = useForm({
    defaultValues: {
      passportNumber: '',
      nationality: '',
    },
  });

  const { passportNumber } = formMethod.watch();
  const [error, setError] = useState<IErrorState | undefined>();

  const passportMutation = useMutation<
    IVerifyPassportResponse,
    AxiosError,
    IVerifyPassportPayload
  >((payload) => verifyPassport(payload), {
    retry: false,
    onSuccess: async ({
      register,
      nation,
      refundAmount,
      email,
      paymentStatus,
    }) => {
      let targetPaymentStatus = paymentStatus;
      let isNeedDepartureDate = false;
      let newEmail = email;
      try {
        const data = await verifyStorePassport({
          passportNumber: passportNumber.toUpperCase(),
        });
        dispatch({
          type: 'UPDATE_USER_INFO',
          payload: {
            refundCondition: data.refundCondition,
            firstRefundDate: data.firstRefundDate,
          },
        });
        newEmail = data.email;
        if (data.refundCondition !== 'REFUND_ONLY') {
          if (!data.register) {
            targetPaymentStatus = 'PAYMENT_WAIT';
          }
          if (!data.departureDate) {
            isNeedDepartureDate = true;
          }
        }
        if (!newEmail) {
          isNeedDepartureDate = true;
        }
      } catch (error) {
        throw error;
      }
      nation && localStorage.setItem('customerNation', nation);
      localStorage.setItem('korAccountSuccess', '0');
      dispatch({
        type: 'UPDATE_USER_INFO',
        payload: {
          passportNumber: passportNumber.toUpperCase(),
          register,
          nationality: nation,
          refundAmount,
          email: newEmail,
          paymentStatus: targetPaymentStatus,
        },
      });
      Sentry.setUser({
        passportNumber: passportNumber.toUpperCase(),
        nationality: nation,
      });
      setError(undefined);
      localStorage.setItem('login', passportNumber);
      if (isNeedDepartureDate) {
        history.push(routes.departureDate);
      } else {
        history.push(register ? routes.home : routes.customerRefund);
      }
    },
    onError: ({ data }: any) => {
      setError({
        message: 'Please verify your Passport Number and try again.',
      });
      if (
        data.code === 'P0009' || // 출국객체를 못 찾을 때
        data.code === 'P0001' || // 여권정보가 없을 때
        data.code === 'R0001' // 환급이 없는 경우
      ) {
        dispatch({
          type: 'UPDATE_HOME_MODAL_STATE',
          payload: {
            isActive: true,
            type: 'CUSTOM',
            data: {
              title: 'Unable to Verify passport.',
              content: `${passportNumber.toUpperCase()}\n\nDid you enter passport number correctly?\nIf yes, press [Confirm] to continue,`,
              subBtnText: 'Cancel',
              btnText: 'Confirm',
              btnCallback: onClickCofirm,
            },
          },
        });
      }
    },
  });

  // 여러번 여권조회 중복호출 방지
  const debouncedMutation = useCallback(
    debounce((formattedPassportNumber) => {
      passportMutation.mutate({ passportNumber: formattedPassportNumber });
    }, 500),
    [passportMutation],
  );

  const onSubmitPassportNumber = () => {
    const formattedPassportNumber = passportNumber
      .toUpperCase()
      .replace(/\s+/g, '');
    debouncedMutation(formattedPassportNumber);
  };

  const onClickCofirm = () => {
    //TODO: 정보 입력 페이지로 이동해 자사로 메일 전송하는 기능 구현
    history.push(routes.refundInfoLeaflet, { passportNumber });
  };

  useEffect(() => {
    const handleBackButton = () => {
      if (currPassportNumber) {
        dispatch({
          type: 'UPDATE_USER_INFO',
          payload: initialGlobalState,
        });
        localStorage.removeItem('login');
        localStorage.removeItem('customerNation');
      }
      window.history.go(1);
    };
    window.addEventListener('popstate', handleBackButton);
    return () => window.removeEventListener('popstate', handleBackButton);
  }, []);

  return (
    <ReceiptLayout>
      <Wrapper>
        <FormProvider {...formMethod}>
          <PassportInputForm
            onSubmit={onSubmitPassportNumber}
            {...(error && { error })}
          />
        </FormProvider>
      </Wrapper>
    </ReceiptLayout>
  );
}

export default PassportValidate;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px 0 100px;
`;
